import { readSlidesData } from '../dom-data.js';
import { trackNamedEvent } from '../gtm.js';
import { scheduleIdle } from '../preload.js';
import { normalizeRoute } from '../routes.js';

export function createSlidesController(root, navigateTo, preloader) {
  if (!root) {
    return null;
  }

  const flow = root.querySelector('.slides-flow');
  if (!flow) {
    return null;
  }

  const introSection = flow.querySelector('.slides-intro');
  const introBg = flow.querySelector('.slides-intro__bg');
  const introTitle = flow.querySelector('.text-card__title');
  const refBtn = flow.querySelector('.slides-intro .ref-button');
  const playBtn = flow.querySelector('.play-button');
  const playerSection = flow.querySelector('.slides-player');
  const video = flow.querySelector('.slides-player__video');
  const audio = flow.querySelector('.slides-player__audio');
  const dots = Array.from(flow.querySelectorAll('.dot'));
  const progressFill = flow.querySelector('#progressFill');
  const hearingIndicator = flow.querySelector('#hearingIndicator');
  const hearingLabel = flow.querySelector('#hearingLabel');
  const replayBtn = flow.querySelector('#replayBtn');
  const forwardBtn = flow.querySelector('#forwardBtn');
  const gradeItems = Array.from(flow.querySelectorAll('.grade-item'));
  const refModal = flow.querySelector('#slidesRefModal');
  const refModalContent = flow.querySelector('#slidesRefModalContent');
  const refModalCloseBtn = flow.querySelector('.slides-ref-modal__close');
  const nextRoute = normalizeRoute(flow.getAttribute('data-next-href')) || 'summary';
  const slides = readSlidesData(flow);

  if (
    !slides.length ||
    !introSection ||
    !introBg ||
    !introTitle ||
    !refBtn ||
    !playBtn ||
    !playerSection ||
    !video ||
    !audio ||
    !progressFill ||
    !hearingIndicator ||
    !hearingLabel ||
    !replayBtn ||
    !forwardBtn ||
    !gradeItems.length ||
    !refModal ||
    !refModalContent
  ) {
    return null;
  }

  const state = {
    index: 0,
    phase: 'intro'
  };

  const hearingLabels = {
    normal: 'NORMAL HEARING',
    mild: 'MILD HEARING LOSS',
    moderate: 'MODERATE HEARING LOSS',
    severe: 'SEVERE HEARING LOSS'
  };

  let active = false;
  let currentGrade = 'normal';
  const warmedGradeAudio = new Set();

  function markActiveDot() {
    dots.forEach(function (dot, index) {
      dot.classList.toggle('active', index === state.index);
    });
  }

  function setForwardEnabled(enabled) {
    forwardBtn.classList.toggle('disabled', !enabled);
  }

  function stopMedia() {
    video.pause();
    audio.pause();

    try { video.currentTime = 0; } catch (e) {}
    try { audio.currentTime = 0; } catch (e) {}
  }

  function getGradeAudioSrc(grade) {
    if (grade === 'mild') return slides[state.index].audioMild;
    if (grade === 'moderate') return slides[state.index].audioModerate;
    if (grade === 'severe') return slides[state.index].audioSevere;
    return '';
  }

  function syncAltAudioToVideo(force) {
    if (currentGrade === 'normal' || !audio.src) {
      return;
    }

    const targetTime = video.currentTime || 0;
    const diff = Math.abs((audio.currentTime || 0) - targetTime);

    if (force || diff > 0.3) {
      try { audio.currentTime = targetTime; } catch (e) {}
    }
  }

  function playAltTrackForGrade(grade) {
    const nextSrc = getGradeAudioSrc(grade);
    if (!nextSrc) {
      return;
    }

    const previousSrc = audio.getAttribute('data-grade-src') || '';
    const shouldReload = previousSrc !== nextSrc;
    const targetTime = video.currentTime || 0;

    function seekAndPlay() {
      try { audio.currentTime = targetTime; } catch (e) {}
      audio.play().catch(function () {});
    }

    if (shouldReload) {
      audio.src = nextSrc;
      audio.setAttribute('data-grade-src', nextSrc);

      const handleLoadedMetadata = function () {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        seekAndPlay();
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.load();
      return;
    }

    seekAndPlay();
  }

  function warmCurrentSlideGradeAudio() {
    const slide = slides[state.index];
    if (!slide) {
      return;
    }

    [slide.audioMild, slide.audioModerate, slide.audioSevere].forEach(function (src) {
      if (!src || warmedGradeAudio.has(src)) {
        return;
      }

      const warmAudio = document.createElement('audio');
      warmAudio.preload = 'auto';
      warmAudio.src = src;
      try {
        warmAudio.load();
      } catch (e) {}
      warmedGradeAudio.add(src);
    });
  }

  function applyGrade(grade) {
    currentGrade = grade;

    gradeItems.forEach(function (item) {
      item.classList.toggle('active', item.dataset.grade === grade);
    });

    hearingLabel.textContent = hearingLabels[grade] || hearingLabels.normal;
    hearingIndicator.classList.toggle('stopped', grade === 'normal');

    if (grade === 'normal') {
      video.muted = false;
      audio.pause();

      try { audio.currentTime = 0; } catch (e) {}

      audio.removeAttribute('src');
      audio.removeAttribute('data-grade-src');
      return;
    }

    video.muted = true;
    playAltTrackForGrade(grade);
  }

  function primeUpcomingAssets() {
    preloader.preloadSlide(state.index);
    preloader.preloadSlide(state.index + 1);

    if (state.index >= slides.length - 2) {
      scheduleIdle(function () {
        preloader.preloadSummary();
      }, 600);
    }
  }

  function renderIntro() {
    const slide = slides[state.index];

    introTitle.innerHTML = slide.title;
    introBg.style.backgroundImage = "url('" + slide.image + "')";
    introSection.hidden = false;
    introSection.style.display = '';
    playerSection.hidden = true;
    playerSection.style.display = '';
    state.phase = 'intro';
    stopMedia();
    applyGrade('normal');
    progressFill.style.width = '0%';
    setForwardEnabled(false);
    markActiveDot();
    primeUpcomingAssets();
  }

  function openReferenceModal() {
    refModalContent.innerHTML = slides[state.index].modal || '';
    refModal.hidden = false;
    refBtn.hidden = true;
  }

  function closeReferenceModal() {
    refModal.hidden = true;
    refBtn.hidden = false;
  }

  function setSlideVideoSource(src) {
    const currentSrc = video.getAttribute('data-slide-src') || '';
    if (currentSrc !== src) {
      video.src = src;
      video.setAttribute('data-slide-src', src);
      video.load();
    }
  }

  function renderPlayer() {
    closeReferenceModal();

    const slide = slides[state.index];
    introSection.hidden = true;
    introSection.style.display = 'none';
    playerSection.hidden = false;
    playerSection.style.display = 'block';
    playerSection.style.backgroundImage = "url('" + slide.image + "')";
    progressFill.style.width = '0%';
    setForwardEnabled(false);
    state.phase = 'player';

    setSlideVideoSource(slide.video);

    try { video.currentTime = 0; } catch (e) {}

    video.play().catch(function () {});
    applyGrade('normal');
    warmCurrentSlideGradeAudio();
    primeUpcomingAssets();
  }

  function goToNextSlide(playImmediately) {
    state.index += 1;

    if (state.index >= slides.length) {
      navigateTo(nextRoute);
      return;
    }

    if (playImmediately) {
      closeReferenceModal();
      renderPlayer();
      return;
    }

    renderIntro();
    refModalContent.innerHTML = slides[state.index].modal || '';
    refBtn.hidden = false;
  }

  playBtn.addEventListener('click', function (event) {
    if (!active) return;
    event.preventDefault();
    renderPlayer();
  });

  refBtn.addEventListener('click', function () {
    if (!active) return;
    openReferenceModal();
  });

  if (refModalCloseBtn) {
    refModalCloseBtn.addEventListener('click', function () {
      if (!active) return;
      closeReferenceModal();
    });
  }

  replayBtn.addEventListener('click', function (event) {
    if (!active) return;
    event.preventDefault();
    trackNamedEvent(root, 'replay');
    renderPlayer();
  });

  forwardBtn.addEventListener('click', function (event) {
    if (!active) return;
    event.preventDefault();

    if (forwardBtn.classList.contains('disabled')) {
      return;
    }

    trackNamedEvent(root, 'next_slide');
    goToNextSlide();
  });

  gradeItems.forEach(function (item) {
    item.addEventListener('click', function () {
      if (!active) return;
      applyGrade(item.dataset.grade || 'normal');
    });
  });

  video.addEventListener('timeupdate', function () {
    if (!video.duration) return;

    const progress = Math.min(100, (video.currentTime / video.duration) * 100);
    progressFill.style.width = progress + '%';
    syncAltAudioToVideo(false);
  });

  video.addEventListener('ended', function () {
    setForwardEnabled(true);
    audio.pause();
  });

  video.addEventListener('seeking', function () {
    syncAltAudioToVideo(true);
  });

  video.addEventListener('play', function () {
    if (currentGrade !== 'normal') {
      playAltTrackForGrade(currentGrade);
    }
  });

  video.addEventListener('pause', function () {
    if (currentGrade !== 'normal') {
      audio.pause();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!active) return;

    if (event.key === 'Escape' && !refModal.hidden) {
      closeReferenceModal();
    }
  });

  return {
    enter: function () {
      active = true;
      state.index = 0;
      renderIntro();
      refModalContent.innerHTML = slides[state.index].modal || '';
      refBtn.hidden = false;
    },
    leave: function () {
      active = false;
      closeReferenceModal();
      stopMedia();
    }
  };
}
